import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	forwardRef,
	HostListener,
	Inject,
	Injector,
	Input,
	OnDestroy,
	OnInit,
	Output,
	Type,
	ViewChild
} from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AlreadyAddedValuesPipe } from 'lu-already-added-values-pipe';
import { LuIconType } from 'lu-icon';
import { SearchPipe } from 'lu-search-pipe';
import {
	DataService,
	DsConfig,
	KEY_DOWN_ARROW,
	KEY_ENTER,
	KEY_ESCAPE,
	KEY_SPACE,
	KEY_UP_ARROW,
	ScrollService
} from 'lu-services';
import {
	BehaviorSubject,
	debounceTime,
	distinctUntilChanged,
	finalize,
	Observable,
	pairwise,
	Subject,
	Subscription,
	tap
} from 'rxjs';
import { LuSearchOptions } from './lu-search/lu-search.component';
import {
	dataService as DataServiceToken,
	LuSelectOptions,
	LuSelectUrlOptions,
	scrollService as ScrollServiceToken
} from './lu-select.models';
import { LuSelectService } from './lu-select.service';

@Component({
	// eslint-disable-next-line @angular-eslint/component-selector
	selector: 'lu-select',
	templateUrl: './lu-select.component.html',
	styleUrls: ['./lu-select.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => LuSelectComponent),
			multi: true
		},
		SearchPipe,
		AlreadyAddedValuesPipe
	]
})
export class LuSelectComponent implements ControlValueAccessor, OnInit, OnDestroy {
	/**
	 * @Input() options: LuSelectOptions
	 * This input is used to configure the select component. It accepts an object of type LuSelectOptions.
	 */
	@Input() options: LuSelectOptions = null;
	/**
	 * @Input() urlOptions: LuSelectUrlOptions
	 * This input is used to configure the API requests made by the select component. It accepts an object of type LuSelectUrlOptions.
	 */
	@Input() urlOptions: LuSelectUrlOptions;
	/**
	 * @Input() displayClearButton: boolean
	 * This input determines whether a clear button should be displayed in the select component.
	 */
	@Input() displayClearButton: boolean;
	/**
	 * @Input() parentForm: NgForm
	 * This input is used to provide the parent form of the select component. It is used for form validation.
	 */
	@Input() parentForm: FormGroupDirective;
	/**
	 * @Input() set items(value: Array<any>)
	 * This input is used to provide the items that should be displayed in the select dropdown. It accepts an array of any type.
	 */
	@Input() set items(value: Array<any>) {
		this.originalItems = value;
		this._items$.next(value);
		// when we have selected item and/or default item in parent component and we are
		// using api mode, we pass only that objects through items input and we need
		// to exclude only that items in all future requests from this component
		if (value.length > 0 && this.urlOptions && this.urlOptions.apiUrl) {
			value.forEach(item => {
				if (item.hasOwnProperty('id')) {
					this.excludeItems.push(item.id);
				}
			});
		}
	}

	/**
	 * @public get items$(): Observable<any[]>
	 * This getter returns an Observable of the items that should be displayed in the select dropdown.
	 */
	public get items$(): Observable<any[]> {
		return this._items$.asObservable();
	}

	/**
	 * @Output() selectInstanceReady: EventEmitter<LuSelectService>
	 * This output emits an event when the select component instance is ready. It emits an instance of LuSelectService.
	 */
	@Output() selectInstanceReady: EventEmitter<LuSelectService> = new EventEmitter<LuSelectService>();
	/**
	 * @Output() loadedData: EventEmitter<LuSelectService>
	 * This output emits an event when data is loaded in the select component. It emits an instance of LuSelectService.
	 */
	@Output() loadedData: EventEmitter<LuSelectService> = new EventEmitter<LuSelectService>();

	/**
	 * @ViewChild('singleItemEl') singleItemEl: ElementRef<HTMLDivElement>
	 * This ViewChild provides a reference to the HTML element that displays the selected item in the select component.
	 */
	@ViewChild('singleItemEl') singleItemEl: ElementRef<HTMLDivElement>;
	/**
	 * @ViewChild('itemsBox', { static: true }) itemsBox: ElementRef<HTMLDivElement>
	 * This ViewChild provides a reference to the HTML element that contains the dropdown items in the select component.
	 */
	@ViewChild('itemsBox', { static: true }) itemsBox: ElementRef<HTMLDivElement>;

	/**
	 * The selected item from the dropdown.
	 */
	selectedItem: any;

	/**
	 * A boolean indicating whether the dropdown is open or not.
	 */
	isOpen = false;

	/**
	 * The placeholder text to be displayed in the dropdown.
	 */
	placeholder: string;

	/**
	 * The debounce time (in milliseconds) for the search input.
	 */
	debounceTime = 300;

	/**
	 * The scroll distance (in pixels) for the dropdown.
	 */
	scrollDistance = 80;

	/**
	 * The property by which the dropdown items should be grouped.
	 */
	groupBy: string;

	/**
	 * The position of the dropdown section. Can be either 'top' or 'bottom'.
	 */
	sectionPosition: 'top' | 'bottom';

	/**
	 * The live announcer message for accessibility.
	 */
	liveAnnouncer: string;

	/**
	 * A boolean indicating whether the dropdown menu is up or not.
	 */
	isDDMUp: boolean;

	/**
	 * The type of the icon to be used in the dropdown.
	 */
	LuIconType = LuIconType;

	/**
	 * A BehaviorSubject that emits the changes in the search input.
	 */
	inputChange$ = new BehaviorSubject<object>({ value: '', key: '' });

	/**
	 * The original items to be displayed in the dropdown.
	 */
	originalItems = [];

	/**
	 * The current value of the search input.
	 */
	searchValue = '';

	/**
	 * A boolean indicating whether the search input should be displayed or not.
	 */
	showSearch = false;

	/**
	 * The search options for the search input.
	 */
	searchOptions: LuSearchOptions = {
		placeHolder: ''
	};

	/**
	 * Subject that emits the changes in the value.
	 */
	private changeValue$ = new Subject<string>();

	/**
	 * The property by which the dropdown items should be filtered.
	 */
	private filterBy: string;

	/**
	 * Instance of LuSelectService used for handling select operations.
	 */
	private selectService: LuSelectService;

	/**
	 * Instance of NgControl used for handling form control operations.
	 */
	private ngControl: NgControl;

	/**
	 * Array of items to be excluded from the dropdown.
	 */
	private excludeItems = [];

	/**
	 * The current position of the cursor in the dropdown.
	 */
	private cursor = 0;

	/**
	 * The apiUrl to be used for API requests.
	 */
	private apiUrl: string;

	/**
	 * The parameters to be used for API requests.
	 */
	private params: any = {};

	/**
	 * The current page number for paginated API requests.
	 */
	private page: number;

	/**
	 * The property by which the dropdown items should be selected.
	 */
	private selectAs: string;

	/**
	 * A boolean indicating whether it's the first load of the dropdown or not.
	 */
	private firstLoad = true;

	/**
	 * A boolean indicating whether there are more items to be loaded from the API.
	 */
	private hasNext = true;

	/**
	 * The height of the select element.
	 */
	private selectElementHeight = 40;

	/**
	 * The maximum height of the dropdown.
	 */
	private ddlMaxHeight = 200;

	/**
	 * Subscription to the HTTP requests made by the component.
	 */
	private httpSubscription: Subscription = new Subscription();

	/**
	 * Subscription to the value changes of the component.
	 */
	private valueChangeSub: Subscription;

	/**
	 * Subscription to the input changes of the component.
	 */
	private inputChangeSub: Subscription;

	/**
	 * List of items displayed in dropdown *(display filtered data)
	 */
	// tslint:disable-next-line:variable-name
	private _items$ = new BehaviorSubject<any[]>(null);
	/**
	 * Getter for valueChanges$.
	 * @returns {Observable<string>} An Observable that emits the changes in the value.
	 */
	get valueChanges$(): Observable<string> {
		return this.changeValue$.asObservable();
	}

	/**
	 * Observable for tracking loading state
	 */
	private loadingData$ = new BehaviorSubject<boolean>(false);

	constructor(
		@Inject(ElementRef) private elementRef: ElementRef,
		@Inject(ChangeDetectorRef) private changeDetectorRef: ChangeDetectorRef,
		@Inject(DataServiceToken) private dataService: DataService,
		@Inject(ScrollServiceToken) private scrollService: ScrollService,
		@Inject(TranslateService) private translate: TranslateService,
		@Inject(SearchPipe) private searchPipe: SearchPipe,
		@Inject(AlreadyAddedValuesPipe) private alreadyAddedValuesPipe: AlreadyAddedValuesPipe,
		@Inject(Injector) private inj: Injector
	) {
		this.selectService = new LuSelectService(this);
		this.searchOptions.placeHolder = this.translate.instant('search');
	}

	/**
	 * @HostListener('window:click', ['$event'])
	 * This method handles click events on the window.
	 * If the click event target is not contained within the select component and the dropdown is open, the dropdown is closed.
	 *
	 * @param {MouseEvent} event - The MouseEvent object associated with the click event.
	 */
	@HostListener('window:click', ['$event'])
	handleClick(event: MouseEvent): void {
		if (!this.elementRef.nativeElement.contains(event.target) && this.isOpen) {
			this.closeDropdown();
		}
	}

	/**
	 * @HostListener('keydown', ['$event'])
	 * This method handles keydown events on the host element. It provides keyboard navigation functionality for the dropdown.
	 * If the dropdown is grouped, keyboard navigation is not supported and the method will return.
	 * If the clear button is focused and a selected item exists, hitting Enter or Space will clear the selection.
	 * The method handles the following keys:
	 * - Up Arrow: Scrolls up in the dropdown.
	 * - Down Arrow: Scrolls down in the dropdown.
	 * - Escape: Closes the dropdown.
	 * - Enter: Selects the current item or clears the selection if the clear button is focused.
	 * - Space: Selects the current item or clears the selection if the clear button is focused.
	 * 					If the search input is focused, the default action is prevented to avoid form submission.
	 *
	 * @param {KeyboardEvent} event - The KeyboardEvent object associated with the keydown event.
	 */
	@HostListener('keydown', ['$event'])
	handleKeyupEvent(event: KeyboardEvent): void {
		const targetElement = event.target as HTMLElement;
		// navigation using keyboard not supported with groupBy
		if (!!this.groupBy) {
			return;
		}
		// serves as a condition for clearSelection key event
		const shouldClearSelection = document.activeElement.id === 'clearButtonIcon' && !!this.selectedItem;
		switch (event.key) {
			case KEY_UP_ARROW:
				event.preventDefault();
				this.keyScrollUp();
				break;
			case KEY_DOWN_ARROW:
				event.preventDefault();
				this.keyScrollDown();
				break;
			case KEY_ESCAPE:
				this.closeDropdown();
				break;
			case KEY_ENTER:
				this.handleSelection(shouldClearSelection);
				break;
			case KEY_SPACE:
				// Prevent form submission if user hits space key while input (search) is focused
				if (targetElement.tagName.toLowerCase() === 'input') {
					return;
				}
				this.handleSelection(shouldClearSelection);
				break;
		}
	}

	/**
	 * This method is called when a dropdown item is hovered over. It sets the cursor to the index of the hovered item.
	 *
	 * @param {number} i - The index of the hovered item.
	 */
	onHover(i): void {
		this.cursor = i;
	}

	onGroupItemHover(item): void {
		this.cursor = this._items$.value.indexOf(item);
	}

	/**
	 * This method sets the cursor to 0 if it is not already set. This is used to reset the cursor position.
	 */
	setCursor(): void {
		if (!!this.cursor) {
			return;
		}
		this.cursor = 0;
	}

	/**
	 * Handles the selection of an item in the dropdown.
	 * If the dropdown is disabled or readonly, the method will return.
	 * If the clear button is focused and a selected item exists, the selection will be cleared.
	 * If the dropdown is not open, it will be opened and a message will be announced to the screen reader.
	 * If none of the above conditions are met, the current item will be selected,
	 * the selection will be sent to the parent component, and the dropdown will be closed.
	 *
	 * @param {boolean} shouldClearSelection - A boolean indicating whether the selection should be cleared.
	 * This is true if the clear button is focused and a selected item exists.
	 */
	private handleSelection(shouldClearSelection: boolean): void {
		const { disabled, readonly } = this.options;

		if (disabled || readonly) {
			return;
		}

		if (shouldClearSelection) {
			this.clearSelection();
			return;
		}

		if (!this.isOpen) {
			this.isOpen = true;
			this.setAnnouncer(this.translate.instant('lup.a11y.select.dropdown_opened'), 500);
			return;
		}

		this.selectedItem = this._items$.value[this.cursor];
		this.sendDataToParentComp();
		this.closeDropdown();
	}

	ngOnInit(): void {
		// @ts-ignore
		this.ngControl = this.inj.get<NgControl>(NgControl as Type<NgControl>);
		this.loadOptions();
		this.loadUrlOptions();
		this.loadDataOnInit();
		this.selectInstanceReady.emit(this.selectService);
		this.listenToInputChange();
	}

	/**
	 * This method is triggered when the position of the dropdown changes.
	 * It sets the sectionPosition to the new position and triggers change detection to update the view.
	 *
	 * @param {('top' | 'bottom')} $event - The new position of the dropdown. Can be either 'top' or 'bottom'.
	 */
	onPositionChange($event: 'top' | 'bottom'): void {
		this.sectionPosition = $event;
		this.changeDetectorRef.detectChanges();
	}

	/**
	 * This method is used to toggle the visibility of the dropdown list.
	 * If the dropdown is disabled or readonly, the method will return.
	 * If the dropdown is not open, it checks if there is enough space to open the dropdown downwards.
	 * If there isn't, it sets isDDMUp to true to open the dropdown upwards.
	 * It then sets isOpen to true to open the dropdown and calls loadDataOnOpen to load the data.
	 * If the dropdown is already open, it sets isOpen to false to close the dropdown.
	 */
	toggleList(): void {
		const { disabled, readonly } = this.options;
		if (disabled || readonly) {
			return;
		}
		if (!this.isOpen) {
			// tslint:disable-next-line:max-line-length
			const totalHeightWhenExpanded =
				this.elementRef.nativeElement.getBoundingClientRect().bottom + this.selectElementHeight + this.ddlMaxHeight;
			this.isDDMUp = window.innerHeight < totalHeightWhenExpanded;
		}
		this.isOpen = !this.isOpen;
		if (!this.cursor) {
			this.cursor = 0;
		}
	}

	clearSelection(event?: Event): void {
		event?.stopPropagation();
		this.selectedItem = null;
		this.sendDataToParentComp();
	}

	clearSearch(): void {
		this.inputChange$.next({ value: '', key: '' });
		this.searchValue = '';
	}

	selectItem(item: any): void {
		const { disabled, readonly } = this.options;
		if (disabled || readonly) {
			return;
		}

		this.selectedItem = item;
		this.sendDataToParentComp();
		this.closeDropdown();
	}

	isActive(item: any, index: number): boolean {
		// if (this.selectedItem) {
		// 	return this.selectedItem === item || index === this.cursor;
		// }
		return index === this.cursor;
	}

	isGroupItemActive(item: any): boolean {
		return this._items$.value.indexOf(item) === this.cursor;
	}

	isValid(): boolean {
		let valid = true;
		if (this.ngControl && (this.ngControl.touched || (this.parentForm && this.parentForm.submitted))) {
			valid = this.ngControl.valid;
		}
		return valid;
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouch = fn;
	}

	onScrollDown(): void {
		if (!!this.apiUrl) {
			this.loadData();
		}
	}

	trackByFn = (index: number, item: any): any => {
		if (!item) {
			return;
		}

		return !!this.selectAs ? item[this.selectAs] : item;
	};

	/**
	 * This method is used to update the value of the select component.
	 * It is called when the parent component changes the value of the select component.
	 * If the selectAs property is set, it will find the item in the dropdown that matches the new value and select it.
	 * If the selectAs property is not set, it will directly set the selected item to the new value.
	 * After setting the selected item, it will update the cursor to the index of the selected item in the dropdown.
	 *
	 * @param {any} newModel - The new value for the select component.
	 */
	writeValue(newModel: any): void {
		if (this.selectAs) {
			this.selectedItem = this._items$.value.filter(item => item[this.selectAs] === newModel)[0];
			if (!!this.selectedItem) {
				this.cursor = this._items$.value.indexOf(this.selectedItem);
			}
			return;
		}
		this.selectedItem = newModel;

		if (!!this.selectedItem) {
			this.cursor = this._items$.value.indexOf(this.selectedItem);
		}
	}

	/**
	 * This method sets up listeners for changes in the search input.
	 * It subscribes to two observables: valueChanges$ and inputChange$.
	 *
	 * The valueChanges$ observable is used to perform a search operation whenever the search input changes.
	 * It uses the debounceTime operator to delay the search operation by 300 milliseconds to avoid making too many requests.
	 * The distinctUntilChanged operator is used to ignore duplicate consecutive items.
	 * The tap operator is used to perform the search operation with the current search value.
	 *
	 * The inputChange$ observable is used to handle special cases when the backspace key is pressed.
	 * It uses the pairwise operator to get the previous and current search values.
	 * If the previous value is empty and the backspace key is pressed, the last selected item is deleted.
	 * If the previous value is not empty, a letter is deleted from the search input.
	 * The tap operator is used to handle these cases.
	 */
	private listenToInputChange(): void {
		this.valueChangeSub = this.valueChanges$
			.pipe(
				debounceTime(300),
				distinctUntilChanged(),
				tap(val => {
					this.triggerSearch(val, false);
				})
			)
			.subscribe();

		// We need to track both previous and current value here, if previous value is empty
		// and we hit backspace we want to delete last selected element, if previous value is not empty, we delete letter
		// That's why we use observable with pairwise
		this.inputChangeSub = this.inputChange$
			.pipe(
				pairwise(),
				tap(([prev, curr]) => {
					this.onInputChange([prev, curr]);
				})
			)
			.subscribe();
	}

	private onInputChange(events): void {
		const currentEvent = events[1];
		if (currentEvent.key === KEY_ENTER || currentEvent.key === KEY_UP_ARROW || currentEvent.key === KEY_DOWN_ARROW) {
			return;
		}

		// check if input is an object or string or whatever, should be triggered only if correct type
		this.searchValue = currentEvent.value;
		this.changeValue$.next(currentEvent.value);
	}

	private triggerSearch(value: string, loadMore: boolean): void {
		if (!this.urlOptions) {
			this._items$.next(this.searchPipe.transform(this.originalItems, value, this.filterBy));
			this.cursor = 0;
			// tslint:disable-next-line:no-unused-expression
			!!this.itemsBox && (this.itemsBox.nativeElement.scrollTop = 0);
		} else {
			this.search(value, loadMore);
		}
	}

	/**
	 * This method is used to perform a search operation in the dropdown.
	 * If the loadMore parameter is true and there are no more items to load (hasNext is false),
	 * or if there are no urlOptions, the method will return.
	 * If there is an existing httpSubscription, it is unsubscribed.
	 * The loading spinner is shown and a loading message is announced to the screen reader.
	 * If loadMore is false, the firstLoad property is set to true and the page number is reset to 1.
	 * If there is a page number, it is added to the request parameters.
	 * The search value is added to the request parameters.
	 * A DsConfig object is created with the API URL and the request parameters.
	 * The primarySpinnerConfig.show property of the DsConfig object is set to false to hide the spinner during the request.
	 * A GET request is made to the API using the dataService's getList method.
	 * When the request is successful, the hasNext property is updated with the has_next property from the response.
	 * The firstLoad property is set to false.
	 * If loadMore is true, the new items from the response are added to the current items.
	 * If loadMore is false, the current items are replaced with the new items from the response.
	 * The cursor is reset to 0 and the dropdown is scrolled to the top.
	 * An announcer message is set based on whether there are more items to load or not.
	 * The page number is incremented.
	 *
	 * @param {string} [val] - The search value. Optional.
	 * @param {boolean} [loadMore] - A boolean indicating whether more items should be loaded. Optional.
	 */
	private search(val?: string, loadMore?: boolean): void {
		if ((!!loadMore && !this.hasNext) || !this.urlOptions) {
			return;
		}
		if (this.httpSubscription) {
			this.httpSubscription.unsubscribe();
		}
		this.showLoading();
		this.setAnnouncer(this.translate.instant('lup.typeahead.loading'), 1000);
		if (!loadMore) {
			this.firstLoad = true;
			this.page = 1;
		}
		if (!!this.page) {
			this.params.page = this.page;
		}
		this.params[this.filterBy] = val;
		const dsConfig: DsConfig = this.dataService.getDefaultConfig(this.apiUrl, this.params);
		dsConfig.primarySpinnerConfig.show = false;
		this.httpSubscription = this.dataService
			.getList<any>(dsConfig)
			.pipe(finalize(() => this.hideLoading()))
			.subscribe(result => {
				const { data: newItems, has_next } = result;
				this.hasNext = !!has_next;

				this.firstLoad = false;
				if (!!loadMore) {
					this.originalItems = [...this.originalItems, ...newItems];
					this._items$.next([...this._items$.getValue(), ...newItems]);
				} else {
					this.originalItems = newItems;
					this._items$.next(newItems);

					if (!!this.cursor) {
						this.cursor = 0;
					}

					if (!!this.itemsBox) {
						this.itemsBox.nativeElement.scrollTop = 0;
					}
				}

				const resultsAnnouncerString = this.hasNext
					? 'lup.typeahead.results_more_than'
					: 'lup.typeahead.results_less_than';
				this.setAnnouncer(this.translate.instant(resultsAnnouncerString, { result_number: this._items$.value.length }));

				this.page++;
			});
	}

	private hideLoading(): void {
		this.loadingData$.next(false);
	}

	private showLoading(): void {
		this.loadingData$.next(true);
	}

	private keyScrollDown(): void {
		if (!this.itemsBox) {
			return;
		}

		const indexOfNextEl = this.cursor + 2; // List starts from 1, curosr from 0, so we have to increment twice here
		if (this.scrollService.keyScrollDown(this.itemsBox, indexOfNextEl)) {
			this.cursor++;
		}
	}

	private keyScrollUp(): void {
		if (!this.itemsBox) {
			return;
		}

		// List starts from 1, cursor from 0, so we don't need to decrement here
		if (this.scrollService.keyScrollUp(this.itemsBox, this.cursor)) {
			this.cursor--;
		}
	}

	private onChange: any = () => {};

	private onTouch: any = () => {};

	private closeDropdown(): void {
		this.isOpen = false;
		if (!this.cursor) {
			this.cursor = 0;
		}
		this.clearSearch();
	}

	/**
	 * This method is used to load data for the dropdown.
	 * If there are no more items to load (hasNext is false), the method will return.
	 * If there is a page number, it is added to the request parameters.
	 * The items to be excluded are also added to the request parameters.
	 * A DsConfig object is created with the API URL and the request parameters.
	 * The primarySpinnerConfig.show property of the DsConfig object is set to false to hide the spinner during the request.
	 * A GET request is made to the API using the dataService's getList method.
	 * When the request is successful, the hasNext property is updated with the has_next property from the response.
	 * The firstLoad property is set to false.
	 * The items from the response are added to the current items and the _items$ BehaviorSubject is updated with the new items.
	 * An announcer message is set based on whether there are more items to load or not.
	 * The page number is incremented.
	 * The loadedData event is emitted with the selectService instance.
	 */
	private loadData(): void {
		if (!!this.hasNext) {
			if (!!this.page) {
				this.params.page = this.page;
			}
			this.params.exclude = this.excludeItems;

			const dsConfig: DsConfig = this.dataService.getDefaultConfig(this.apiUrl, this.params);
			dsConfig.primarySpinnerConfig.show = false;
			this.dataService.getList<any>(dsConfig).subscribe(({ data, has_next }) => {
				this.hasNext = !!has_next;

				this.firstLoad = false;
				this.originalItems = [...this.originalItems, ...data];
				this._items$.next([...this._items$.getValue(), ...data]);

				const resultsAnnouncerString = this.hasNext
					? 'lup.a11y.select.results_more_than'
					: 'lup.a11y.select.results_less_than';
				this.setAnnouncer(this.translate.instant(resultsAnnouncerString, { result_number: this._items$.value.length }));

				this.page++;
				this.loadedData.emit(this.selectService);
			});
		}
	}

	private loadOptions(): void {
		if (!this.options) {
			return;
		}

		const { paginate, selectAs, groupBy, placeholder, filterBy, showSearch } = this.options;

		if (!!paginate) {
			this.page = 1;
		}

		this.showSearch = showSearch;
		this.filterBy = filterBy;
		this.selectAs = selectAs || null;
		this.groupBy = groupBy || null;
		this.placeholder = placeholder || this.translate.instant('lup.a11y.select.default_placeholder');
	}

	private sendDataToParentComp(): void {
		if (this.selectAs && this.selectedItem && this.selectedItem.hasOwnProperty(this.selectAs)) {
			this.onChange(this.selectedItem[this.selectAs]);
		} else {
			this.onChange(this.selectedItem);
		}
		if (this.selectedItem) {
			this.setAnnouncer(
				`${this.selectedItem[this.selectService.getSelectItemField()]} ${this.translate.instant(
					'lup.a11y.select.selected_item'
				)}`,
				500
			);
		}
	}

	private loadUrlOptions(): void {
		if (!this.urlOptions) {
			return;
		}

		if (!!this.urlOptions.apiUrl) {
			this.apiUrl = this.urlOptions.apiUrl;
		}

		if (!!this.urlOptions.params) {
			this.params = this.urlOptions.params;
		}

		const { apiUrl, params } = this.urlOptions;

		this.apiUrl = apiUrl;
		this.params = params || {};
	}

	/**
	 * Sets the live announcer message for accessibility and clears it after a specified duration.
	 * This method is used to announce specific actions to the screen reader.
	 *
	 * @param {string} value - The message to be announced.
	 * @param {number} [duration=2000] - The duration in milliseconds after which the message will be cleared. Defaults to 2000.
	 */
	private setAnnouncer(value: string, duration: number = 2000): void {
		this.liveAnnouncer = value;
		setTimeout(() => {
			this.liveAnnouncer = '';
		}, duration);
	}
	private loadDataOnInit(): void {
		const { readonly, disabled } = this.options;
		if (readonly || disabled) {
			return;
		}
		if (!this.urlOptions?.apiUrl) {
			return;
		}
		if (this._items$.value.length) {
			return;
		}
		this.loadData();
	}

	ngOnDestroy(): void {
		if (this.valueChangeSub) {
			this.valueChangeSub.unsubscribe();
		}
		if (this.inputChangeSub) {
			this.inputChangeSub.unsubscribe();
		}
	}
}
